import type { SVGProps } from "react";

/**
 * Line icons used across the booking page. They all inherit `currentColor`
 * so a parent can tint them (white, cyan when selected, muted when disabled).
 */
type IconProps = SVGProps<SVGSVGElement>;

function Icon({ children, ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {children}
    </svg>
  );
}

export function GridIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3.5" y="3.5" width="7" height="7" rx="1.6" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="1.6" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="1.6" />
      <rect x="13.5" y="13.5" width="7" height="7" rx="1.6" />
    </Icon>
  );
}

export function CalendarIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3.5" y="5" width="17" height="15.5" rx="3" />
      <path d="M3.5 9.75h17M8 3.5v3M16 3.5v3" />
    </Icon>
  );
}

export function CalendarCheckIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3" y="5" width="14.5" height="15.5" rx="3" />
      <path d="M3 9.75h14.5M7 3.5v3M13.5 3.5v3" />
      <path d="m15 17.5 1.9 1.9 3.6-4" />
    </Icon>
  );
}

export function CalendarClockIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3" y="5" width="14.5" height="15.5" rx="3" />
      <path d="M3 9.75h14.5M7 3.5v3M13.5 3.5v3" />
      <circle cx="17.75" cy="17.75" r="3.75" />
      <path d="M17.75 16v1.9l1.3.85" />
    </Icon>
  );
}

export function LayersIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="m12 3 8.5 4.25L12 11.5 3.5 7.25z" />
      <path d="m3.5 12 8.5 4.25L20.5 12M3.5 16.75 12 21l8.5-4.25" />
    </Icon>
  );
}

export function PhoneIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M6.2 3.5h2.3l1.6 4-2 1.3a11.5 11.5 0 0 0 5.1 5.1l1.3-2 4 1.6v2.3a2.7 2.7 0 0 1-2.94 2.69C9.36 18.03 5.97 14.64 3.5 6.44A2.7 2.7 0 0 1 6.2 3.5Z" />
    </Icon>
  );
}

/**
 * The two-tone "bounding box" motif the design repeats next to every hero and
 * help item: a square whose lower half and lower corner handles are cyan.
 */
export function FrameIcon(props: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      <path d="M5 6.5v11M19 6.5v11M5 5h14" stroke="rgba(255,255,255,0.62)" />
      <path d="M5 19h14" stroke="#0CA5F0" />
      <circle cx="5" cy="5" r="2.6" fill="#C6CBD2" stroke="none" />
      <circle cx="19" cy="5" r="2.6" fill="#C6CBD2" stroke="none" />
      <circle cx="5" cy="19" r="2.6" fill="#0CA5F0" stroke="none" />
      <circle cx="19" cy="19" r="2.6" fill="#0CA5F0" stroke="none" />
    </svg>
  );
}

export function ClockIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 1.75" />
    </Icon>
  );
}

export function TimerIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="13.5" r="7.5" />
      <path d="M12 10v3.5M9.5 2.5h5M18.5 7l1.5-1.5" />
    </Icon>
  );
}

export function WalletIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3" y="6" width="18" height="13" rx="3" />
      <path d="M3 10h13a2 2 0 0 1 2 2v1a2 2 0 0 1-2 2H3" />
    </Icon>
  );
}

export function CheckCircleIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="m8.5 12.25 2.4 2.4 4.6-5" />
    </Icon>
  );
}

export function ShieldIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M12 3 5 5.75V12c0 4.2 2.85 7.4 7 9 4.15-1.6 7-4.8 7-9V5.75z" />
      <path d="m9.25 12 2 2 3.5-3.75" />
    </Icon>
  );
}

export function UserIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="8.25" r="3.75" />
      <path d="M4.75 20c.6-3.6 3.6-5.75 7.25-5.75S18.65 16.4 19.25 20" />
    </Icon>
  );
}

export function IdeaIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M9.25 17.5h5.5M10 20.5h4" />
      <path d="M12 3.5a5.75 5.75 0 0 1 3.6 10.24c-.55.44-.85 1.1-.85 1.76H9.25c0-.66-.3-1.32-.85-1.76A5.75 5.75 0 0 1 12 3.5Z" />
    </Icon>
  );
}

export function ListIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M9 6.5h11M9 12h11M9 17.5h11" />
      <path d="M4.5 6.5h.01M4.5 12h.01M4.5 17.5h.01" />
    </Icon>
  );
}

export function RouteIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M6.5 20V9.5a3 3 0 0 1 3-3h5a3 3 0 0 0 3-3" />
      <circle cx="6.5" cy="20" r="1.6" />
      <circle cx="17.5" cy="3.5" r="1.6" />
      <path d="M10 13.5h7" />
    </Icon>
  );
}

export function DesignIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="4" y="3.5" width="7" height="7" rx="3.5" />
      <rect x="13" y="3.5" width="7" height="7" rx="3.5" />
      <rect x="4" y="13.5" width="7" height="7" rx="3.5" />
      <path d="M13 17h7" />
    </Icon>
  );
}

export function BrandingIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4.5 19.5 8 16m0 0 8.75-8.75a2.47 2.47 0 0 0-3.5-3.5L4.5 12.5V16z" />
      <path d="M14 20h6" />
    </Icon>
  );
}

export function MobileIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="7" y="2.75" width="10" height="18.5" rx="3" />
      <path d="M11 18.5h2" />
    </Icon>
  );
}

export function AiIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="7.5" y="7.5" width="9" height="9" rx="2.5" />
      <path d="M10.5 2.5v3M13.5 2.5v3M10.5 18.5v3M13.5 18.5v3M2.5 10.5h3M2.5 13.5h3M18.5 10.5h3M18.5 13.5h3" />
    </Icon>
  );
}

export function ChatIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M20.5 12.25c0 4.14-3.8 7.5-8.5 7.5-.9 0-1.77-.12-2.58-.35L4.5 21l1.3-3.63a7 7 0 0 1-2.3-5.12c0-4.14 3.8-7.5 8.5-7.5s8.5 3.36 8.5 7.5Z" />
    </Icon>
  );
}

export function HeadsetIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4.5 14v-2a7.5 7.5 0 0 1 15 0v2" />
      <rect x="2.75" y="13" width="4" height="6" rx="2" />
      <rect x="17.25" y="13" width="4" height="6" rx="2" />
      <path d="M19.5 19v.75a2.25 2.25 0 0 1-2.25 2.25H13" />
    </Icon>
  );
}

export function ArrowLeftIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M14.5 5.5 8 12l6.5 6.5" />
    </Icon>
  );
}

export function ArrowRightIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M9.5 5.5 16 12l-6.5 6.5" />
    </Icon>
  );
}

export function WhatsappIcon(props: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.86 9.86 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm0 18.15h-.01a8.2 8.2 0 0 1-4.18-1.15l-.3-.18-3.11.82.83-3.04-.2-.31a8.17 8.17 0 0 1-1.25-4.38c0-4.54 3.7-8.24 8.24-8.24a8.18 8.18 0 0 1 5.82 2.42 8.18 8.18 0 0 1 2.41 5.83c0 4.54-3.7 8.23-8.25 8.23Zm4.52-6.16c-.25-.13-1.47-.72-1.69-.8-.23-.09-.39-.13-.56.12-.16.25-.64.8-.79.97-.14.16-.29.19-.54.06-.25-.12-1.05-.38-2-1.23a7.5 7.5 0 0 1-1.38-1.72c-.15-.25-.02-.38.11-.5.11-.11.25-.29.37-.44.13-.15.17-.25.25-.41.09-.17.04-.31-.02-.44-.06-.12-.56-1.35-.77-1.85-.2-.48-.4-.42-.56-.42h-.47c-.16 0-.43.06-.65.31-.22.25-.85.83-.85 2.03s.87 2.35.99 2.51c.12.17 1.71 2.61 4.14 3.66.58.25 1.03.4 1.38.51.58.18 1.11.16 1.53.1.47-.07 1.47-.6 1.68-1.18.2-.58.2-1.07.14-1.18-.06-.11-.22-.17-.47-.29Z" />
    </svg>
  );
}

/** Boxed grid — used by the "product enquiry" consultation type. */
export function ProductIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="none" {...props}>
      <path
        d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M4 7.5 12 12l8-4.5M12 12v9" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}
